import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../contexts/AuthContext';
import { redirectBasedOnPermission } from '../../../utils/methods';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import AuthService from '../../../services/auth.service';
import { Modules, NonAuthRoutes, RoleCodes } from '../../../enums';

interface LocationState {
  userId: number;
  mfaMethod: 'totp' | 'email_otp';
}

const MfaVerify: React.FC = () => {
  const { setLoggedInUser, setToken, setPermissions, setIsSystemAdmin } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state as LocationState | null;

  useEffect(() => {
    if (!state?.userId) {
      navigate(Modules.auth + NonAuthRoutes.login, { replace: true });
    } else if (state.mfaMethod === 'email_otp') {
      AuthService.sendOtp(state.userId).catch(() => {});
    }
  }, []);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event && event.preventDefault();
      if (!state?.userId) return;

      if (!code.trim() || !/^\d{6}$/.test(code.trim())) {
        setError(t('mfaVerify.pageTexts.form.code.errors.invalid'));
        return;
      }

      setLoading(true);
      setError('');

      try {
        let response: any;
        if (state.mfaMethod === 'totp') {
          response = await AuthService.verifyTotp(state.userId, code.trim());
        } else {
          response = await AuthService.verifyOtp(state.userId, code.trim());
        }

        if (response.status === 'success') {
          const data = response.data;
          const user = data.user ?? {};
          const userPermissions = user.permissions ?? [];
          const isSystemAdmin = user.role === RoleCodes.admin;

          setLoggedInUser(user);
          setToken(data.token ?? '');
          setPermissions(userPermissions);
          setIsSystemAdmin(isSystemAdmin);

          navigate(
            redirectBasedOnPermission(isSystemAdmin, userPermissions) ||
              Modules.error + NonAuthRoutes.forbidden
          );
        }
      } catch (err: any) {
        setError(err?.response?.data?.message ?? t('mfaVerify.pageTexts.form.code.errors.invalid'));
      } finally {
        setLoading(false);
      }
    },
    [code, state, setLoggedInUser, setToken, setPermissions, setIsSystemAdmin, navigate, t]
  );

  const handleResend = useCallback(async () => {
    if (!state?.userId) return;
    setResending(true);
    try {
      await AuthService.sendOtp(state.userId);
    } finally {
      setResending(false);
    }
  }, [state]);

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  if (!state?.userId) return null;

  return (
    <div className="w-px-400 mx-auto pt-5 pt-lg-0">
      <div className="mb-4">
        <h4 className="mb-1">{t('mfaVerify.pageTexts.title')}</h4>
        <p className="mb-0 text-muted">
          {state.mfaMethod === 'totp'
            ? t('mfaVerify.pageTexts.totpDescription')
            : t('mfaVerify.pageTexts.otpDescription')}
        </p>
      </div>
      <form id="formMfaVerify" className="mb-5" autoComplete="off" onSubmit={handleSubmit}>
        <div className="form-floating form-floating-outline mb-5">
          <Input
            type="text"
            id="code"
            name="code"
            error={!!error}
            errorMessage={error}
            label={t('mfaVerify.pageTexts.form.code.label')}
            placeholder={t('mfaVerify.pageTexts.form.code.placeholder')}
            value={code}
            onKeyDown={handleKeyDown}
            onChange={(_e: any, _name: string) => {
              setCode(_e.target.value);
              setError('');
            }}
            onBlur={() => {}}
          />
        </div>
        <Button
          className="btn btn-primary d-grid w-100 mb-3"
          text={t('mfaVerify.pageTexts.form.submitButton')}
          disabled={loading}
          loading={loading}
          onClick={handleSubmit}
        />
        {state.mfaMethod === 'email_otp' && (
          <Button
            className="btn btn-outline-secondary d-grid w-100"
            text={t('mfaVerify.pageTexts.form.resendButton')}
            disabled={resending}
            loading={resending}
            onClick={handleResend}
          />
        )}
      </form>
    </div>
  );
};

export default MfaVerify;
